import { IBrowseFilter } from './browseQuery';

export type ChipGroup = 'ranks' | 'categories' | 'parts' | 'types' | 'level';

export interface IFilterChip {
    group: ChipGroup;
    label: string;
    //  Ids to deselect when this chip's X is clicked. A collapsed parent chip
    //  carries every child id it stands for.
    removeIds: number[];
}

interface Parent { label: string; childIds: number[]; }

export interface ChipLabelSource {
    rank(id: number): string;
    category(id: number): string;
    part(id: number): string;
    type(id: number): string;
    categoryParents: Parent[];
    partParents: Parent[];
    typeParents: Parent[];
}

//  Replace every fully-selected parent's children with one parent chip. This is
//  what keeps the chip row survivable when someone selects a whole subtree.
function collapse(
    selected: number[],
    parents: Parent[],
    group: ChipGroup,
    label: (id: number) => string,
): IFilterChip[] {
    const set = new Set(selected);
    const covered = new Set<number>();
    const chips: IFilterChip[] = [];

    for (const p of parents) {
        if (p.childIds.length && p.childIds.every((c) => set.has(c))) {
            chips.push({ group, label: p.label, removeIds: [...p.childIds] });
            for (const c of p.childIds) {
                covered.add(c);
            }
        }
    }
    for (const id of selected) {
        if (!covered.has(id)) {
            chips.push({ group, label: label(id), removeIds: [id] });
        }
    }
    return chips;
}

export function buildChips(f: IBrowseFilter, labels: ChipLabelSource): IFilterChip[] {
    const chips: IFilterChip[] = [];
    chips.push(...collapse(f.ranks, [], 'ranks', labels.rank));
    chips.push(...collapse(f.categories, labels.categoryParents, 'categories', labels.category));
    chips.push(...collapse(f.parts, labels.partParents, 'parts', labels.part));
    chips.push(...collapse(f.types, labels.typeParents, 'types', labels.type));
    if (f.minLevel !== 0 || f.maxLevel !== 100) {
        chips.push({ group: 'level', label: `Lv ${f.minLevel} to ${f.maxLevel}`, removeIds: [] });
    }
    return chips;
}
